import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSharedPlaylist } from '../api/authClient';
import { trackLinkKey } from '../api/itunesClient';
import { API_BASE } from '../api/config';
import { useAppleMusicSongLinks } from '../hooks/useAppleMusicSongLinks';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { formatTrackLinks, generateCSV, DEFAULT_MUSIC_SERVICE, MUSIC_SERVICE_LABELS } from '../utils/exportUtils';
import MusicServiceIcon from './MusicServiceIcon';
import '../styles/share.css';

function SharePage({ playlistId }) {
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkFormat, setLinkFormat] = useState('with-names');
  const audio = useAudioPreview({});

  // The exporter's music service is baked into the shared playlist;
  // old shares predate the field and default to Spotify.
  const musicService = playlist?.musicService || DEFAULT_MUSIC_SERVICE;
  const musicServiceLabel = MUSIC_SERVICE_LABELS[musicService] || MUSIC_SERVICE_LABELS[DEFAULT_MUSIC_SERVICE];

  useEffect(() => {
    getSharedPlaylist(playlistId)
      .then((data) => setPlaylist(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [playlistId]);

  // Live-resolved canonical Apple Music links. Apple-music shares bake
  // whatever was resolved at share time; this upgrades the remaining search
  // URLs (and all older shares) as lookups complete, so taps deep-link into
  // the Music app instead of stranding on its home screen.
  const appleMusicLinks = useAppleMusicSongLinks(playlist?.trackList, musicService);

  const linkedTracks = useMemo(
    () => (playlist?.trackList || []).map((t) => ({
      ...t,
      externalUrl: t.deezerUrl || t.externalUrl,
      musicUrl: appleMusicLinks.get(trackLinkKey(t)) || t.musicUrl || t.spotifyUrl,
    })),
    [playlist, appleMusicLinks]
  );

  const handleCopyLinks = useCallback(async () => {
    if (linkedTracks.length === 0) return;
    const text = formatTrackLinks(linkedTracks, linkFormat, musicService);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [linkedTracks, linkFormat, musicService]);

  const handleExportCSV = useCallback(() => {
    if (!playlist || linkedTracks.length === 0) return;
    const csv = generateCSV(linkedTracks, musicService);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(playlist.playlistName || 'playlist').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [playlist, linkedTracks, musicService]);

  const handlePreviewToggle = useCallback(
    (track) => {
      if (audio.isPlaying && audio.currentTrack?.id === track.name) {
        audio.pause();
      } else {
        audio.play({ ...track, id: track.name });
      }
    },
    [audio]
  );

  if (loading) {
    return (
      <div className="share-page">
        <div className="share-loading">
          <div className="share-loading-spinner" />
          <p>Loading playlist...</p>
        </div>
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <div className="share-page">
        <div className="share-error">
          <h2>Playlist Not Found</h2>
          <p>This playlist may have been removed or the link is invalid.</p>
          <a href="/" className="share-cta-btn">Create Your Own Galaxy</a>
        </div>
      </div>
    );
  }

  const { playlistName, trackList, seedArtists } = playlist;

  return (
    <div className="share-page">
      {/* Header */}
      <header className="share-header">
        <a href="/" className="share-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
            <circle cx="12" cy="12" r="3" />
            <circle cx="12" cy="12" r="8" opacity="0.4" />
            <circle cx="12" cy="12" r="11" opacity="0.15" />
          </svg>
          Stellar
        </a>
        <a href="/" className="share-cta-link">Create Your Own Galaxy</a>
      </header>

      {/* Playlist Info */}
      <div className="share-content">
        <div className="share-info">
          <h1 className="share-title">{playlistName}</h1>
          <div className="share-meta">
            <span>{trackList.length} track{trackList.length !== 1 ? 's' : ''}</span>
            {seedArtists && seedArtists.length > 0 && (
              <span className="share-meta-seeds">
                from {seedArtists.join(', ')}
              </span>
            )}
          </div>
          <div className="share-badge">Generated with Stellar</div>
        </div>

        {/* Actions */}
        <div className="share-actions">
          {navigator.share && (
            <button
              className="share-action-btn share-action-btn-native"
              onClick={async () => {
                try {
                  // API_BASE first: in the Capacitor app the page URL is
                  // capacitor://localhost/..., which navigator.share rejects
                  await navigator.share({
                    title: playlistName,
                    text: `Check out this playlist: ${playlistName}`,
                    url: `${API_BASE || window.location.origin}/p/${playlistId}`,
                  });
                } catch {}
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share
            </button>
          )}

          <button className="share-action-btn share-action-btn-csv" onClick={handleExportCSV}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>

          <button className="share-action-btn share-action-btn-copy" onClick={handleCopyLinks}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {copied ? 'Copied!' : `Copy ${musicServiceLabel} Links`}
          </button>

          <div className="share-link-format">
            <button
              className={linkFormat === 'links-only' ? 'active' : ''}
              onClick={() => setLinkFormat('links-only')}
            >
              Links Only
            </button>
            <button
              className={linkFormat === 'with-names' ? 'active' : ''}
              onClick={() => setLinkFormat('with-names')}
            >
              With Names
            </button>
          </div>
        </div>

        {/* Tracklist */}
        <div className="share-tracklist">
          {linkedTracks.map((track, i) => {
            const isCurrentlyPlaying = audio.isPlaying && audio.currentTrack?.id === track.name;

            return (
              <div key={i} className="share-track-row">
                <span className="share-track-number">{i + 1}</span>
                {track.albumImage ? (
                  <img className="share-track-art" src={track.albumImage} alt="" loading="lazy" />
                ) : (
                  <div className="share-track-art-placeholder" />
                )}
                <div className="share-track-info">
                  <span className="share-track-name">{track.name}</span>
                  <span className="share-track-artist">{track.artistName}</span>
                </div>
                {track.previewUrl && (
                  <button
                    className={`share-track-preview-btn ${isCurrentlyPlaying ? 'playing' : ''}`}
                    onClick={() => handlePreviewToggle(track)}
                    title={isCurrentlyPlaying ? 'Pause preview' : 'Play preview'}
                  >
                    {isCurrentlyPlaying ? (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                        <polygon points="6,3 20,12 6,21" />
                      </svg>
                    )}
                  </button>
                )}
                {track.musicUrl && (
                  <a
                    className={`share-track-spotify-link${musicService === 'apple-music' ? ' apple-music' : ''}`}
                    href={track.musicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open in ${musicServiceLabel}`}
                  >
                    <MusicServiceIcon service={musicService} size={14} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mini player bar when preview playing */}
      {audio.currentTrack && (
        <div className="share-player-bar">
          <div className="share-player-info">
            <span className="share-player-name">{audio.currentTrack.name || audio.currentTrack.id}</span>
            <span className="share-player-artist">{audio.currentTrack.artistName}</span>
          </div>
          <button
            className="share-player-toggle"
            onClick={() => audio.toggle()}
          >
            {audio.isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <polygon points="6,3 20,12 6,21" />
              </svg>
            )}
          </button>
        </div>
      )}

      {copied && <div className="share-toast">Copied to clipboard!</div>}
    </div>
  );
}

export default SharePage;
